import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from "js-cookie";
import { useNavigate } from 'react-router-dom';
import { CubeIcon, CurrencyEuroIcon, PresentationChartLineIcon } from "@heroicons/react/20/solid";
import { ShoppingCart, ArrowRight } from 'lucide-react';
import CardProducto from "./CardProducto.jsx";
import DropdownOrder from "./DropdownOrder.jsx";
import DropdownTipoVista from "./DropdownTipoVista.jsx";
import BuscadorInput from "./BuscadorInput.jsx";
import { useCartContext } from '../../components/providers/CartProvider.jsx';
import getMediaUrl, { getDefaultProductImage } from '../../components/tools/utils.jsx';

// ── Sort options ───────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
    { nombre: "Nombre",  valor: "name",       icon: <CubeIcon className="h-4 w-4" /> },
    { nombre: "Precio",  valor: "price",       icon: <CurrencyEuroIcon className="h-4 w-4" /> },
    { nombre: "Novedad", valor: "created_at",  icon: <PresentationChartLineIcon className="h-4 w-4" /> },
];

const VIEW_OPTIONS = [
    { label: '2 columnas', value: { mode: 'grid', columns: 2 } },
    { label: '3 columnas', value: { mode: 'grid', columns: 3 } },
    { label: '4 columnas', value: { mode: 'grid', columns: 4 } },
    { label: 'Lista',      value: { mode: 'list' } },
];

// ── Gradient palette for carousel cards ───────────────────────────────────────
const CARD_GRADIENTS = [
    'from-zinc-700 to-zinc-900',
    'from-red-900 to-zinc-900',
    'from-slate-700 to-slate-900',
    'from-stone-700 to-stone-900',
    'from-neutral-600 to-neutral-900',
    'from-gray-600 to-gray-900',
];

// ── Desktop pill ───────────────────────────────────────────────────────────────
const CategoryPill = ({ label, selected, onClick }) => (
    <button
        onClick={onClick}
        className={`
            relative flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium
            transition-all duration-200 overflow-hidden group
            ${selected
                ? 'bg-brand-dark text-white dark:bg-primary dark:text-primary-content shadow-sm'
                : 'border border-brand-dark/20 dark:border-white/15 text-gray-700 dark:text-gray-300 hover:bg-brand-dark/5 dark:hover:bg-white/10'
            }
        `}
    >
        {label}
        {!selected && (
            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-brand-red transition-[width] duration-300 group-hover:w-full dark:hidden" />
        )}
    </button>
);

// ── Mobile carousel card ───────────────────────────────────────────────────────
const CategoryCard = ({ label, selected, onClick, gradientIdx }) => {
    const grad = CARD_GRADIENTS[gradientIdx % CARD_GRADIENTS.length];
    return (
        <button
            onClick={onClick}
            className={`
                relative flex-shrink-0 snap-start
                w-[38vw] max-w-[160px] h-24
                rounded-2xl overflow-hidden
                flex items-end p-3
                transition-all duration-200 active:scale-95
                bg-gradient-to-br ${grad}
                ${selected ? 'ring-2 ring-brand-red ring-offset-2 ring-offset-base-100' : 'opacity-80'}
            `}
        >
            {/* shimmer overlay when selected */}
            {selected && (
                <span className="absolute inset-0 bg-brand-red/10 pointer-events-none" />
            )}
            <span className={`relative text-sm font-semibold leading-tight text-left
                ${selected ? 'text-white' : 'text-white/80'}`}>
                {label}
            </span>
            {selected && (
                <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-brand-red" />
            )}
        </button>
    );
};

const fmt = (n) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

// ── Main component ─────────────────────────────────────────────────────────────
const ProductsView = () => {
    const navigate = useNavigate();
    const { addToCart } = useCartContext();

    const [products,           setProducts]           = useState([]);
    const [categories,         setCategories]         = useState([]);
    const [loading,            setLoading]            = useState(false);
    const [searchTerm,         setSearchTerm]         = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [addingId,           setAddingId]           = useState(null);

    const handleAddToCart = async (e, product) => {
        e.stopPropagation();
        if (addingId === product.id) return;
        setAddingId(product.id);
        await addToCart(product);
        setTimeout(() => setAddingId(null), 1800);
    };

    const [viewMode, setViewMode] = useState(() => {
        const saved = Cookies.get('viewModeProductos');
        return saved ? JSON.parse(saved) : { mode: 'grid', columns: 3, sortBy: 'created_at', sortDirection: 'asc' };
    });

    // Persist viewMode cookie
    useEffect(() => {
        Cookies.set('viewModeProductos', JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode]);

    // Fetch categories once
    useEffect(() => {
        axios.get('/categories/')
            .then(r => setCategories(Array.isArray(r.data) ? r.data : (r.data.results ?? [])))
            .catch(e => console.error('Error fetching categories:', e));
    }, []);

    // Fetch products on filter change
    useEffect(() => { fetchProducts(); }, [selectedCategories, searchTerm, viewMode.sortBy, viewMode.sortDirection]);

    const fetchProducts = () => {
        setLoading(true);
        const ordering = viewMode.sortDirection === 'asc' ? viewMode.sortBy : `-${viewMode.sortBy}`;
        const params = {
            category: selectedCategories.map(c => c.id).join(','),
            search:   searchTerm,
            ordering,
        };
        axios.get('/products/', { params })
            .then(r => setProducts(Array.isArray(r.data) ? r.data : (r.data.results ?? [])))
            .catch(e => console.error('Error fetching products:', e))
            .finally(() => setLoading(false));
    };

    const toggleCategory = (cat) => {
        setSelectedCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    const clearCategories = () => setSelectedCategories([]);

    // Dynamic grid cols — pre-generate so Tailwind picks them up
    const gridCols = {
        2: 'grid-cols-1 sm:grid-cols-2',
        3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
    };

    return (
        <div className="flex flex-col w-full" style={{ height: '100%', minHeight: 0 }}>

            {/* ── Categories: carousel on mobile, pills on desktop ─────── */}

            {/* Mobile carousel */}
            <div className="md:hidden flex-shrink-0 pt-4 pb-2">
                <div
                    className="flex gap-3 overflow-x-auto scroll-smooth px-4"
                    style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
                >
                    <CategoryCard
                        label="Todos"
                        selected={selectedCategories.length === 0}
                        onClick={clearCategories}
                        gradientIdx={0}
                    />
                    {categories.map((cat, i) => (
                        <CategoryCard
                            key={cat.id}
                            label={cat.name}
                            selected={selectedCategories.includes(cat)}
                            onClick={() => toggleCategory(cat)}
                            gradientIdx={i + 1}
                        />
                    ))}
                    {/* trailing spacer so last card snaps fully into view */}
                    <div className="flex-shrink-0 w-4" />
                </div>
            </div>

            {/* Desktop pills */}
            <div className="hidden md:block flex-shrink-0 px-10 pt-4 pb-2">
                <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                    <CategoryPill
                        label="Todos"
                        selected={selectedCategories.length === 0}
                        onClick={clearCategories}
                    />
                    {categories.map(cat => (
                        <CategoryPill
                            key={cat.id}
                            label={cat.name}
                            selected={selectedCategories.includes(cat)}
                            onClick={() => toggleCategory(cat)}
                        />
                    ))}
                </div>
            </div>

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-4 lg:px-10 py-2">
                <DropdownOrder
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    arrayDatosOrdenacion={SORT_OPTIONS}
                />
                <DropdownTipoVista
                    options={VIEW_OPTIONS}
                    selectedValue={viewMode}
                    onChange={(mode) => setViewMode(prev => ({ ...prev, ...mode }))}
                />
                <BuscadorInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} className="ml-auto" />
            </div>

            {/* ── Products area ─────────────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-scroll px-4 lg:px-10 pb-6">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <span className="loading loading-spinner loading-lg text-brand-red" />
                    </div>
                ) : products.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                        <p className="text-lg font-semibold text-gray-600 dark:text-gray-400">Sin resultados</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                            Prueba a cambiar los filtros o el término de búsqueda.
                        </p>
                    </div>
                ) : viewMode.mode === 'grid' ? (
                    <div className={`grid ${gridCols[viewMode.columns] ?? gridCols[3]} gap-4`}>
                        {products.map(product => (
                            <CardProducto key={product.id} product={product} isAdmin={false} />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {products.map(product => {
                            const imgSrc = getDefaultProductImage(product);
                            const isAdding = addingId === product.id;
                            return (
                                <div
                                    key={product.id}
                                    onClick={() => navigate(`/product/${product.id}`)}
                                    className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer
                                               border border-black/5 dark:border-white/8
                                               bg-white dark:bg-base-300
                                               hover:border-black/10 dark:hover:border-white/15
                                               hover:shadow-sm transition-all duration-200 group"
                                >
                                    {/* Image */}
                                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-base-200">
                                        {imgSrc
                                            ? <img src={getMediaUrl(imgSrc)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                            : <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</div>
                                        }
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm dark:text-white text-gray-900 truncate">
                                            {product.name}
                                        </h3>
                                        {product.short_description && (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                                                {product.short_description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Price */}
                                    <p className="font-bold text-sm dark:text-white text-gray-900 flex-shrink-0">
                                        {fmt(product.price)}
                                    </p>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                        <button
                                            onClick={(e) => handleAddToCart(e, product)}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200
                                                ${isAdding
                                                    ? 'bg-green-500 text-white scale-110'
                                                    : 'border border-black/10 dark:border-white/15 text-gray-500 dark:text-gray-400 hover:bg-brand-red hover:text-white hover:border-brand-red'
                                                }`}
                                        >
                                            <ShoppingCart size={14} />
                                        </button>
                                        <ArrowRight size={14} className="text-gray-300 dark:text-gray-600 group-hover:text-brand-red group-hover:translate-x-0.5 transition-all duration-200" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductsView;
